import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
	emailGetEmailTemplateScores,
	emailClearEmailTemplateScores
} from '@/modules/email/emailActions';
import type { EmailTemplate } from '@/modules/email/emailTypes';
import type { RootState } from '@/rootReducer';
import {
	RateTemplateDefault,
	FourCircles,
	LogoText2,
	RateTemplateIcon,
	RateTemplateGreat,
	RateTempalteOnTrack,
	RateTemplateNeedsAttention,
	RateTemplateInfoIcon
} from '@/assets/images';
import Button from '@/components/Button/newButtons';
import './EmailSmartTemplatesRedesign.scss';

import { sendAnalytics } from '@/services/analyticsService';

interface Props {
	selectedTemplate: EmailTemplate;
}

const EmailSmartTemplatesRedesign = ({
	selectedTemplate
}: Props): JSX.Element => {
	const [
		smartTemplateScores,
		smartTemplateLoading,
		smartTemplateFailure,
		user
	] = useSelector(
		(state: RootState) => [
			state.email.getIn(['templates', 'scores', 'data']).toJS(),
			state.email.getIn(['templates', 'scores', 'loading']),
			state.email.getIn(['templates', 'scores', 'failure']),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);

	const [showSmartTemplates, setShowSmartTemplates] = useState<boolean>(true);
	const dispatch = useDispatch();
	const getEmailTemplateScores = (
		id: number,
		subject: string,
		body: string
	) => dispatch(emailGetEmailTemplateScores({ id, subject, body }));
	const clearTemplateScores = () => dispatch(emailClearEmailTemplateScores());

	useEffect(() => {
		clearTemplateScores();
	}, []);

	useEffect(() => {
		if (smartTemplateLoading) {
			const section2 = document.querySelector(
				'.smart-templates-section-2'
			);
			if (section2) {
				section2.scrollTo({ top: 0, behavior: 'smooth' }); // Smooth scroll to the top
			}
		}
	}, [smartTemplateLoading]);

	const [overallRating, setOverallRating] = useState('Unknown'); // Initialize state

	const calculateOverallRating = scores => {
		// Define categories
		const categories = [];
		if (scores?.subject) categories.push(scores.subject.score);
		if (scores?.body) {
			if (scores?.body.readabilityAndContent)
				categories.push(scores.body.readabilityAndContent.score);
			if (scores?.body.callToAction)
				categories.push(scores.body.callToAction.score);
			if (scores?.body.valueProposition)
				categories.push(scores.body.valueProposition.score);
		}

		// Map score to categories
		const categoryMap = {
			Excellent: 'Great',
			Good: 'Great',
			Medium: 'On the Right Track',
			Bad: 'Needs Attention' // Adjust as necessary for undefined/missing values
		};

		const evaluations = categories.map(
			score => categoryMap[score] || 'Needs Attention'
		);

		// Apply the rules
		if (evaluations.every(evaluation => evaluation === 'Great')) {
			return 'Great';
		} else if (evaluations.includes('Needs Attention')) {
			return 'Needs Attention';
		} else if (evaluations.includes('On the Right Track')) {
			return 'On the Right Track';
		}

		return 'Unknown'; // Fallback
	};

	// Let's calculate our overall score by following our rulesOverall “Great”: All four subcategories must be rated as “Strong.”
	// Overall “On the Right Track”: At least one subcategory is “On the Right Track,” but none are “Needs Attention.”
	// Overall “Needs Attention”: If any subcategory is rated as “Needs Attention,” the overall rating should reflect that.
	useEffect(() => {
		if (smartTemplateScores && selectedTemplate?.id) {
			const overallRating = calculateOverallRating(
				smartTemplateScores[`${selectedTemplate.id}`]
			);
			setOverallRating(overallRating); // Update state
		}
	}, [smartTemplateScores]);

	const getBorderColor = (rating: string) => {
		if (rating === 'Good' || rating === 'Excellent') return '#10A37F'; // Good
		if (rating === 'Medium') return '#39B6E2'; // Average
		return '#E7750E'; // Poor
	};

	const getStatusIcon = (displayCount: string) => {
		if (displayCount === 'Good' || displayCount === 'Excellent') {
			return (
				<div className="template-section-grades text-[#10A37F]">
					<RateTemplateGreat className="far-right-icon" />
					Great
				</div>
			);
		} else if (displayCount === 'Medium') {
			return (
				<div className="template-section-grades text-[#39B6E2]">
					<RateTempalteOnTrack className="far-right-icon" />
					On the Right Track
				</div>
			);
		} else {
			return (
				<div className="template-section-grades text-[#E7750E]">
					<RateTemplateNeedsAttention className="far-right-icon" />
					Needs Attention
				</div>
			);
		}
	};

	const getOverallRatingDisplay = (rating: string) => {
		if (rating === 'Great') {
			return (
				<div className="template-section-grades text-[#10A37F]">
					<RateTemplateGreat className="far-right-icon" /> Great
				</div>
			);
		} else if (rating === 'On the Right Track') {
			return (
				<div className="template-section-grades text-[#39B6E2]">
					<RateTempalteOnTrack className="far-right-icon" /> On the
					Right Track
				</div>
			);
		} else if (rating === 'Needs Attention') {
			return (
				<div className="template-section-grades text-[#E7750E]">
					<RateTemplateNeedsAttention className="far-right-icon" />{' '}
					Needs Attention
				</div>
			);
		} else {
			return (
				<div className="template-section-grades text-gray-500">
					<RateTemplateDefault className="far-right-icon" /> Unknown
				</div>
			);
		}
	};

	const isInSequenceSmartTemplates = !!document.querySelector(
		'.sequence-smart-templates'
	);

	return (
		<div className="smart-templates-redesign smart-templates-wrapper">
			<div
				className={`smart-templates ${
					showSmartTemplates ? '' : 'hide'
				}`}
			>
				<div className="smart-templates-section-1">
					<div className="rate-template-text-header-wrapper">
						<span className="rate-template-header-text">
							{isInSequenceSmartTemplates
								? 'Template Rating'
								: 'Template Overall Rating'}
						</span>
						<div>
							{smartTemplateScores[`${selectedTemplate.id}`] &&
								smartTemplateScores[`${selectedTemplate.id}`]
									.overallGrade && (
									<div>
										{' '}
										{getOverallRatingDisplay(overallRating)}
									</div>
								)}
						</div>
					</div>
				</div>
				<div className="smart-templates-section-2">
					{smartTemplateLoading && (
						<div className="smart-templates-loading-background">
							<div className="loading-rectangle">
								<div className="logo-container">
									<LogoText2 />
								</div>
								<div className="smart-templates-loading-group">
									<span className="loading-text">
										Loading ...
									</span>
									<FourCircles className="ai-loading-inner-right-loading-content-icon" />
								</div>
							</div>
						</div>
					)}
					{smartTemplateFailure && (
						<div className="smart-templates-failure-background">
							<div className="smart-templates-failure-container">
								<span className="smart-templates-failure-text">
									Oops, something went wrong.
									<br />
									<br />
									Please try again by clicking Rate Template
									below.
								</span>
							</div>
						</div>
					)}
					{/* Display this by default before scores are available */}
					{!smartTemplateScores[`${selectedTemplate.id}`] && (
						<>
							<RateTemplateDefault className="rate-template-default" />
							<div className="rate-template-text-wrapper">
								<div>
									Click 'Rate Template' to get AI-powered
									feedback on your template.
								</div>
								<div>
									Get actionable insights to craft more
									engaging emails.
								</div>
							</div>
						</>
					)}
					{/* Display scores after they are retrieved */}
					{smartTemplateScores[`${selectedTemplate.id}`] && (
						<div className="smart-template-scores justify-space-evenly flex h-full flex-col">
							<div className="score-item">
								<div className="score-item-header">
									<span className="label-text">
										Subject Line
										<div className="tooltip-container">
											<RateTemplateInfoIcon className="smart-templates-subject-length-question-mark rate-template-icon" />
											<div className="tooltip-text">
												Evaluates your subject line for
												word count, engagement, and
												potential spam triggers. Aim for
												concise, attention-grabbing
												phrasing to boost open rates.
											</div>
										</div>
									</span>

									{getStatusIcon(
										smartTemplateScores[
											`${selectedTemplate.id}`
										].subject.score
									)}
								</div>
								<div
									className="score-value-container"
									style={{
										border: `1px solid ${getBorderColor(
											smartTemplateScores[
												`${selectedTemplate.id}`
											].subject.score
										)}`,
										borderRadius: '8px',
										padding: '8px'
									}}
								>
									<span className="score-value">
										{
											smartTemplateScores[
												`${selectedTemplate.id}`
											].subject.evaluationText
										}
									</span>
								</div>
							</div>
							<div className="score-item">
								<div className="score-item-header">
									<span className="label-text">
										Body Analysis
										<div className="tooltip-container">
											<RateTemplateInfoIcon className="smart-templates-subject-length-question-mark" />
											<div className="tooltip-text">
												Analyzes your email’s
												readability and clarity. Checks
												for simple language (5th-grade
												level), concise body length,
												clear tone, and presence of
												contact information. Avoid
												overly complex phrases and spam
												triggers.
											</div>
										</div>
									</span>

									{getStatusIcon(
										smartTemplateScores[
											`${selectedTemplate.id}`
										].body.readabilityAndContent.score
									)}
								</div>

								<div
									className="score-value-container"
									style={{
										border: `1px solid ${getBorderColor(
											smartTemplateScores[
												`${selectedTemplate.id}`
											].body.readabilityAndContent.score
										)}`,
										borderRadius: '8px',
										padding: '8px'
									}}
								>
									<span className="score-value">
										{
											smartTemplateScores[
												`${selectedTemplate.id}`
											].body.readabilityAndContent
												.evaluationText
										}
									</span>
								</div>
							</div>
							<div className="score-item">
								<div className="score-item-header">
									<span className="label-text">
										Call to Action
										<div className="tooltip-container">
											<RateTemplateInfoIcon className="smart-templates-subject-length-question-mark" />
											<div className="tooltip-text">
												Reviews the clarity and strength
												of your call to action. Ensure
												your next steps are actionable,
												easy to follow, and encourage
												engagement.
											</div>
										</div>
									</span>
									{getStatusIcon(
										smartTemplateScores[
											`${selectedTemplate.id}`
										].body.callToAction.score
									)}
								</div>

								<div
									className="score-value-container"
									style={{
										border: `1px solid ${getBorderColor(
											smartTemplateScores[
												`${selectedTemplate.id}`
											].body.callToAction.score
										)}`,
										borderRadius: '8px',
										padding: '8px'
									}}
								>
									<span className="score-value">
										{
											smartTemplateScores[
												`${selectedTemplate.id}`
											].body.callToAction.evaluationText
										}
									</span>
								</div>
							</div>
							<div className="score-item">
								<div className="score-item-header">
									<span className="label-text">
										Value Proposition
										<div className="tooltip-container">
											<RateTemplateInfoIcon className="smart-templates-subject-length-question-mark" />
											<div className="tooltip-text">
												Assesses if your email
												communicates a clear value
												proposition. Highlights whether
												you include success metrics,
												examples, or tangible benefits
												that make your message
												compelling.
											</div>
										</div>
									</span>
									{getStatusIcon(
										smartTemplateScores[
											`${selectedTemplate.id}`
										].body.valueProposition.score
									)}
								</div>

								<div
									className="score-value-container"
									style={{
										border: `1px solid ${getBorderColor(
											smartTemplateScores[
												`${selectedTemplate.id}`
											].body.valueProposition.score
										)}`,
										borderRadius: '8px',
										padding: '8px'
									}}
								>
									<span className="score-value">
										{
											smartTemplateScores[
												`${selectedTemplate.id}`
											].body.valueProposition
												.evaluationText
										}
									</span>
								</div>
							</div>
						</div>
					)}
				</div>

				<div
					className={`smart-templates-section-3 ${
						smartTemplateScores[`${selectedTemplate.id}`]
							? 'has-scores'
							: ''
					}`}
				>
					<Button
						variant="primary"
						rightIcon={<RateTemplateIcon />}
						disabled={smartTemplateLoading}
						className={`smart-templates-button ${smartTemplateLoading ? 'disabled-button' : ''}`}
						onClick={() => {
							getEmailTemplateScores(
								selectedTemplate.id,
								selectedTemplate.subject,
								selectedTemplate.body.replace(/<[^>]*>?/gm, '')
							);
							if (user && user.email) {
								sendAnalytics({
									type: 'track',
									userId: user.email,
									properties: {
										templateGraderRedesign:
											user?.features?.templateRedesign
									},
									eventName:
										'User Graded a Templated with Template Grader'
								});
							}
						}}
					>
						{smartTemplateScores[`${selectedTemplate.id}`]
							? 'Rate Again'
							: 'Rate Template'}
					</Button>

					{!smartTemplateScores[`${selectedTemplate.id}`] && (
						<div className="smart-templates-more-information">
							Visit our{' '}
							<a href="https://learn.re2.ai/send-out-high-quality-emails-through-resquared">
								Knowledge Base
							</a>{' '}
							to learn more about the templates
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default EmailSmartTemplatesRedesign;
